import { auth } from "./firebase";
import { authenticatedFetch } from "./api";

// ENG-3454: a password signup is unverified until its owner opens the link, and the backend refuses
// to let an unverified address own an organization.

/** What the backend says when it will not send; the step shows it as is. */
export const VERIFY_EMAIL_FALLBACK_ERROR = "Couldn't send the email. Try again in a minute.";

/**
 * ENG-4080: the backend sends the email (`POST /send-verification-email`), in Haven's layout and with a
 * link to this site's own page, where Firebase's `sendEmailVerification` sent its template and a link to
 * a `firebaseapp.com` page. Resolves whether it sent or had nothing to send ("not_needed": already
 * verified); throws an Error carrying the server's message otherwise.
 */
export async function sendVerificationEmail(): Promise<void> {
  if (!auth.currentUser) return;
  let response: Response;
  try {
    response = await authenticatedFetch("/send-verification-email", { method: "POST" }, { scoped: false });
  } catch {
    // "Failed to fetch", a timeout, a token that could not be had: the person gets the standard line.
    throw new Error(VERIFY_EMAIL_FALLBACK_ERROR);
  }
  if (response.ok) return;
  let message = VERIFY_EMAIL_FALLBACK_ERROR;
  // Only the two answers the route words for people (429 limit, 503 not sent). A 401's "Invalid or missing
  // Firebase ID token" and a proxy's body are not for the screen.
  if (response.status === 429 || response.status === 503) {
    try {
      const body = (await response.json()) as { error?: unknown };
      if (typeof body.error === "string" && body.error) message = body.error;
    } catch { /* not JSON: keep the fallback */ }
  }
  throw new Error(message);
}

/** The signed-in address while it awaits its link, else null: Google and Apple sign-ins arrive verified. */
export function unverifiedEmail(): string | null {
  const user = auth.currentUser;
  return user && !user.emailVerified && user.providerData.some((p) => p.providerId === "password") ? user.email : null;
}

/**
 * The backend reads `email_verified` from the ID token, not the user record. A page load refreshes the
 * record but keeps the cached token, so `emailVerified` can be true while the token still says false
 * for up to an hour: compare against the token itself.
 */
export async function refreshTokenIfNewlyVerified(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  if (!user.emailVerified) await user.reload();
  const current = auth.currentUser;
  if (!current?.emailVerified) return;
  const { claims } = await current.getIdTokenResult();
  if (claims.email_verified !== true) await current.getIdToken(true);
}
